/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { selectCurrentEmpid, selectCurrentUser } from '../../../pages/login/authSlice';
import { useGetScorecardVersionsBySupervisorQuery } from '../coachingActionPlanSlice';
import './managerCAP.scss';
import ActionPlans from '../prepareStep/components/ActionPlans/ActionPlans';
import { useGetSupervisorsByManagerQuery } from '../activeActionPlans/activeActionPlansSlice';
import { AssociateScoresTable } from '../prepareStep/components/AssociateScoresTable';
import ActiveActionPlans from '../activeActionPlans/ActiveActionPlans';
import AssociateAcknowledge from '../associateAcknowledge/AssociateAcknowledge';
import MessageInfo from '../../../components/messageInfo/MessageInfo';
import CoachingActionPlanScoreCardDashboard from '../prepareStep/components/CoachingActionPlanScoreCardDashboard';

type ActiveTab = 'dashboard' | 'actionPlans';

export const ManagerCAP: React.FC = () => {
    const loggedEmpid = useSelector(selectCurrentEmpid);
    const loggedUser = useSelector(selectCurrentUser);

    // ── Shared filter state (passed down to tab components) ──────────────────
    const [selectedTeamLead, setSelectedTeamLead] = useState<number | null>(null);
    const [selectedScorecard, setSelectedScorecard] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<ActiveTab>('actionPlans');
    const [isActionPlansExpanded, setIsActionPlansExpanded] = useState(false);
    const [showAcknowledge, setShowAcknowledge] = useState(false);
    const [selectedActionPlanId, setSelectedActionPlanId] = useState<number | null>(null);
    const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

    // ── Scorecard versions (reuse the supervisor endpoint) ───────────────────    
    const { data: teamLeadsData } = useGetSupervisorsByManagerQuery(
        { employeeNumber: parseInt(loggedEmpid, 10), includeHierarchy: true },
        { skip: !loggedEmpid }
    );
    const { data: scorecardData } = useGetScorecardVersionsBySupervisorQuery(
        selectedTeamLead?.toString(),
        { skip: !selectedTeamLead }
    );
    const scorecardOptions = useMemo(() => scorecardData?.data ?? [], [scorecardData]);

    useEffect(() => {
        if (scorecardOptions.length > 0 && !selectedScorecard) {
            setSelectedScorecard(scorecardOptions[0].scorecard_version_id);
        }
    }, [scorecardOptions]); // eslint-disable-line


    const teamLeadOptions = useMemo(() => {
        const leads = teamLeadsData?.data ?? [];
        if (leads?.length === 0) return [];
        return [
            ...leads.map((tl: any) => ({
                label: tl.employee_name,
                value: tl.employee_number,
            })),
        ];
    }, [teamLeadsData]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="mgr-cap-page">

            {!showAcknowledge && (
                <div className="card shadow bg-body rounded mt-3 border-0">
                    <div className="card-body p-3">
                        <div className="row">
                            <div className="col-md-4 text-start">
                                <span id="label_employeename" className="mt-1 ms-3 d-inline-block">
                                    {`Hello ${loggedUser}!`}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!showAcknowledge && <h2 className="mgr-cap-heading">Coaching</h2>}
            {(showAcknowledge) ? (
                <AssociateAcknowledge
                    actionPlanId={selectedActionPlanId ?? 0}
                    sessionId={selectedSessionId ?? 0}
                    onBack={() => setShowAcknowledge(false)}
                    via="managerCAP"
                />
            ) :
                (
                    <div className="card shadow bg-body rounded mt-3 border-0">
                        <div className="card-body p-4">

                            {/* ── Top filters: Team Lead + Scorecard ── */}
                            <div className="mgr-top-filters">
                                <div className="mgr-filter-group">
                                    <h6>Team Lead</h6>
                                    <Dropdown
                                        value={selectedTeamLead}
                                        options={teamLeadOptions}
                                        onChange={(e) => setSelectedTeamLead(e.value)}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select"
                                        className="custom-dropdown"
                                    />
                                </div>

                                <div className="mgr-filter-group">
                                    <h6>Scorecard</h6>
                                    <Dropdown
                                        value={selectedScorecard}
                                        options={scorecardOptions}
                                        onChange={(e) => setSelectedScorecard(e.value)}
                                        optionLabel="scorecard_name"
                                        optionValue="scorecard_version_id"
                                        placeholder="Select"
                                        className="custom-dropdown"
                                    />
                                </div>
                            </div>

                            {/* ── Tabs ── */}
                            <div className="mgr-tabs">
                                <button
                                    className={`mgr-tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('dashboard')}
                                >
                                    Dashboard
                                </button>
                                <button
                                    className={`mgr-tab-btn ${activeTab === 'actionPlans' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('actionPlans')}
                                >
                                    Action Plans
                                </button>
                            </div>

                            {/* ── Tab content ── */}
                            {activeTab === 'dashboard' && (
                                <div>
                                    {(!selectedTeamLead || !selectedScorecard) ? (
                                        <MessageInfo
                                            title=""
                                            message="Please select a Team Lead and Scorecard above."
                                            rowHeight="15vh"
                                        />
                                    ) : (<>
                                        <div className='pt-3'>
                                            <Accordion activeIndex={0}>
                                                <AccordionTab header="Prepare: Agent Performance">
                                                    <h5>Last 30 Day Rollup</h5>
                                                    <CoachingActionPlanScoreCardDashboard
                                                        empNum={selectedTeamLead}
                                                        inputmonth={new Date().getMonth() + 1}
                                                        inputyear={new Date().getFullYear()}
                                                        scorecardVersion={selectedScorecard}
                                                    />
                                                </AccordionTab>
                                            </Accordion>
                                        </div>
                                        <div className='row' style={{ paddingTop: '10px' }}>
                                            <div className={isActionPlansExpanded ? 'col-md-7' : 'col-md-11'}>
                                                <div className="mt-4">
                                                    <div className="flex justify-content-between align-items-center mb-3">                                                        
                                                        <h4>Next Best Action</h4>

                                                    </div>
                                                    <AssociateScoresTable via='managerCAP'
                                                        teamLeadId={selectedTeamLead ?? 0} scorecardVersionId={selectedScorecard ?? 0} />
                                                </div>
                                            </div>
                                            <div className={isActionPlansExpanded ? 'col-md-5 pt-2' : 'col-md-1'}>
                                                <span
                                                    className={isActionPlansExpanded ? "expand-icon" : "expand-icon collapsed-action-plans"}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setIsActionPlansExpanded(prev => !prev)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setIsActionPlansExpanded(prev => !prev);
                                                        }
                                                    }}
                                                >
                                                    <i className="pi pi-chevron-down" /> Active Action Plans
                                                </span>
                                                <div style={{ display: isActionPlansExpanded ? 'inline' : 'none' }}>
                                                    <ActiveActionPlans via='managerCAP'
                                                        teamLeadId={selectedTeamLead ?? 0}
                                                        scorecardVersionId={selectedScorecard ?? 0}
                                                        setActionPlanId={setSelectedActionPlanId}
                                                        setSessionId={setSelectedSessionId}
                                                        setShowAcknowledge={setShowAcknowledge}
                                                    />
                                                </div>
                                            </div></div>
                                    </>)}
                                </div>
                            )}

                            {activeTab === 'actionPlans' && (
                                <div>
                                    {(!selectedTeamLead && !selectedScorecard) ? (
                                        <MessageInfo
                                            title="Please select a Team Lead and Scorecard above."
                                            message=''
                                            rowHeight="15vh"
                                        />
                                    ) : (
                                        <ActionPlans
                                            via='managerCAP'
                                            teamLeadEmployeeId={selectedTeamLead ?? 0}
                                            scorecardVersionId={selectedScorecard ?? 0}
                                            setActionPlanId={setSelectedActionPlanId}
                                            setSessionId={setSelectedSessionId}
                                            setShowAcknowledge={setShowAcknowledge}
                                            emptyMessage="No results found."
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
        </div>
    );
};

export default ManagerCAP;